import { Box } from '@mui/material';
import FlexBox from 'Components/FlexBox';
import Icon, { TIconName } from 'DesignSystem/Primitives/Icon/Icon';
import ThemeTypography from 'DesignSystem/Primitives/Typography/ThemeTypography';
import { useDynamicMaxWidths } from 'hooks/useDynamicMaxWidths';
import React, { FC, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { ImageVariant, IMediaItem } from 'FeatureModules/Media/types/media.types';
import Image from '../Image/Image';

export interface ItemSummaryDetails {
  icon: TIconName;
  title: string;
  /**
   * Optional link for the detail item that routes to a different page on click
   */
  navigateTo?: string;
}

interface IItemSummaryProps {
  image?: ImageVariant | IMediaItem | null;
  imagePlaceholder?: TIconName;
  title: string;
  externalId?: string;
  extraInfo?: string;
  TitleEndAdornment?: ReactNode;
  details: (ItemSummaryDetails | boolean)[];
  size?: 'medium' | 'large';
}

const ItemSummary: FC<IItemSummaryProps> = ({
  title,
  image,
  imagePlaceholder,
  externalId,
  extraInfo,
  details,
  size = 'medium',
  TitleEndAdornment,
}) => {
  const navigate = useNavigate();
  const detailsFiltered = details.filter(Boolean) as ItemSummaryDetails[];
  const { containerRef, setItemRef, maxWidths } = useDynamicMaxWidths(detailsFiltered.length, 1);

  const onDetailsClick = (navigateTo: string) => {
    navigate?.(navigateTo);
  };

  return (
    <FlexBox width="100%" gap={2}>
      {!!image && <Image width={44} height={44} image={image} />}

      {!image && imagePlaceholder && (
        <Box display="flex" bgcolor="white" borderRadius="6px" padding="4px">
          <Icon name={imagePlaceholder} size="xx-large" color="blue-ice" />
        </Box>
      )}

      <Box width="calc(100% - 60px)">
        <FlexBox gap={size === 'medium' ? 0.8 : 1.5}>
          <ThemeTypography variant={size === 'medium' ? 'ITEM_TITLE' : 'TITLE_MEDIUM'} autoOverflow>
            {title}
          </ThemeTypography>
          {externalId && (
            <FlexBox maxWidth="30%">
              <ThemeTypography variant="BODY_MEDIUM" color="GRAY_80" mr={1}>
                |
              </ThemeTypography>
              <ThemeTypography variant="BODY_MEDIUM" autoOverflow color="GRAY_80">
                {externalId}
              </ThemeTypography>
            </FlexBox>
          )}
          {extraInfo && (
            <FlexBox maxWidth="30%">
              <ThemeTypography variant="BODY_MEDIUM" color="GRAY_80" mr={1}>
                |
              </ThemeTypography>
              <ThemeTypography variant="BODY_MEDIUM" autoOverflow color="GRAY_80">
                {extraInfo}
              </ThemeTypography>
            </FlexBox>
          )}

          {TitleEndAdornment}
        </FlexBox>

        <FlexBox gap={1} maxWidth="100%" mt={0.5} ref={containerRef}>
          {detailsFiltered.filter(Boolean).map(({ icon, title, navigateTo }, index) => (
            <FlexBox
              gap={0.5}
              key={`${title}-${index}`}
              ref={setItemRef(index)}
              sx={{ cursor: navigateTo ? 'pointer' : 'default', minWidth: 0 }}
              maxWidth={maxWidths[index] || 'none'}
              onClick={() => (navigateTo ? onDetailsClick(navigateTo) : undefined)}
            >
              <Icon name={icon} size="small" color="gray-60" />
              <ThemeTypography variant="LABEL_INPUT" autoOverflow>
                {title}
              </ThemeTypography>
            </FlexBox>
          ))}
        </FlexBox>
      </Box>
    </FlexBox>
  );
};

export default ItemSummary;
